import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ConflictsApi } from "apis/ConflictsApi";
import { ManagedStocksApi } from "apis/ManagedStocksApi";
import { StoreFunctionsApi } from "apis/StoreFunctionsApi";
import { BaseState } from "models/common/baseState";
import { BulkEditManagedStock } from "models/daoModels/managedStock/BulkEditManagedStock";
import { LinkRequest, MergeRequest } from "models/daoModels/managedStock/MergeRequest";
import { ManagedStockAddUpdate, ManagedStockView } from "models/dbModels/managedStock/ManagedStock";
import { ManagedStockSync } from "models/dbModels/managedStock/ManagedStockSync";
import { ProductImport, ProductImportExtended } from "models/dbModels/managedStock/OptionalManagedStockUpdate";
import { ManagedStockConflictSearchFilterQuery } from "models/filters/conflicts/ManagedStockConflictSearchFilterQuery";
import { ManagedStockSearchFilterQuery, ManagedStockSearchFilterQueryLoadDetails } from "models/filters/managedStocks/ManagedStockSearchFilterQuery";

export const getStocks = createAsyncThunk("stocksSlice/getStocks", async (params?: ManagedStockSearchFilterQuery) => {
    const response = await ManagedStocksApi.Search(params ?? {});
    return response.data;
});

export const getStocksOptimized = createAsyncThunk("stocksSlice/getStocks", async (params?: ManagedStockSearchFilterQuery) => {
    const response = await ManagedStocksApi.OptimsedSearch(params ?? {});
    return response.data;
});

// export const getStocks = createAsyncThunk<PaginatedResponse<ManagedStockView[]>, ManagedStockSearchFilterQuery | undefined>(
//     'stocksSlice/getStocks',
//     async (params?: ManagedStockSearchFilterQuery) => {
//         const response = await axios.post<PaginatedResponse<ManagedStockView[]>>(
//             `api/v1/managedStocks/search`,
//             { ...params },
//             { headers: { "Content-Encoding": "gzip" } }
//         );
//         return response.data;  // Ensure that the data returned here matches PaginatedResponse<ManagedStockView[]>
//     }
// );

export const getStockTable = createAsyncThunk("stocksSlice/getStockTable", async (params?: ManagedStockSearchFilterQuery) => {
    params = { ...params, limit: params?.limit ?? 100, offset: params?.offset ?? 0, contains: params?.contains ?? true, loadDetails: params?.loadDetails ?? [] };
    const response = await ManagedStocksApi.Search(params);
    return response.data;
});

export const getStockTableOptimised = createAsyncThunk("stocksSlice/getStockTable", async (params?: ManagedStockSearchFilterQuery) => {
    params = { ...params, loadDetails: params?.loadDetails ?? [] };
    const response = await ManagedStocksApi.OptimsedSearch(params);
    return response.data;
});

export const getStock = createAsyncThunk("stocksSlice/getStock", async (stockId: any) => {
    const response = await ManagedStocksApi.Search({ ids: [stockId], loadDetails: [ManagedStockSearchFilterQueryLoadDetails.Pdes, ManagedStockSearchFilterQueryLoadDetails.Apns] });
    return response.data;
});

export const getDispensePricing = createAsyncThunk("stocksSlice/getDispensePricing", async (stockId: string) => {
    const response = await StoreFunctionsApi.DispensePricingSearch({ stockIds: [stockId] });
    return response.data;
});

export const createStock = createAsyncThunk("stocksSlice/createStock", async (stocks: ManagedStockAddUpdate[]) => {
    const response = await ManagedStocksApi.Add(stocks);
    return response.data;
});

export const updateStock = createAsyncThunk("stocksSlice/updateStock", async (stocks: ManagedStockAddUpdate[]) => {
    const response = await ManagedStocksApi.Update(stocks);
    return response.data;
});

export const updateMultipleStocks = createAsyncThunk("stocksSlice/updateMultipleStocks", async (stocks: ManagedStockAddUpdate[]) => {
    const response = await ManagedStocksApi.Update(stocks);
    return response.data;
});

export const deleteStocks = createAsyncThunk("stocksSlice/deleteStock", async (params?: ManagedStockSearchFilterQuery) => {
    params = { ...params, loadDetails: params?.loadDetails ?? [] };
    const response = await ManagedStocksApi.Delete(params ?? {});
    return response.data;
});

export const syncStocks = createAsyncThunk("stocksSlice/syncStocks", async (payload: ManagedStockSync) => {
    const response = await ManagedStocksApi.SyncChanges(payload);
    return response.data;
});

export const bulkUpdate = createAsyncThunk("stocksSlice/bulkUpdate", async (params: BulkEditManagedStock) => {
    const response = await ManagedStocksApi.BulkUpdate(params);
    return response.data;
});

export const getStockConflicts = createAsyncThunk("stocksSlice/getStockConflicts", async (params?: ManagedStockConflictSearchFilterQuery) => {
    const response = await ConflictsApi.managedStockConflictSearch(params ?? {});
    return response.data;
});


export const linkToProduct = createAsyncThunk("stocksSlice/linkToProduct", async (linkRequests: LinkRequest[]) => {
    const response = await ManagedStocksApi.LinkToProduct(linkRequests);
    return response.data;
});

export const importFromProductsWithData = createAsyncThunk("stocksSlice/importFromProductsWithData", async (modelImports: ProductImportExtended[]) => {
    const response = await ManagedStocksApi.ImportFromProductsWithData(modelImports);
    return response.data;
});

export const mergeStocks = createAsyncThunk("stocksSlice/mergeStocks", async (mergeRequests: MergeRequest[]) => {
    const response = await ManagedStocksApi.Merge(mergeRequests);
    return response.data;
});

interface StateProps<T> extends BaseState<T> {
    gp: number,
    markup: number,
    stores: any[],
    storeDispensePricing: any[],
    showPdePopup: boolean,
    showApnPopup: boolean,
    systemStocksPopup: boolean,
    manualMergePopup: boolean,
    bulkEditPopup: boolean,
    bulkEditWizardPopup: boolean,
    loading: boolean,
    loadingSync: boolean,
    saveSuccess: boolean,
    storeStocks: any[],
    stockGroupsDropdown: any[],
    keyword: string,
    totalRows: number,
    totalPages: number,
    pageIndex: number,
    pageSize: number,
    selectedRows: { position: number, id: number }[][],
    deselectedRows: { position: number, id: number }[][],
    selectedRowsCount: number,
    error: string,
    selectAllFilter: boolean,
    table: ManagedStockView[],
}

const initialModel: BaseState<ManagedStockView> = {
    model: {
        id: 0,
        stockId: "",
        name: "",
        dispenseName: null,
        storeGroupId: 0,
        supplierId: null,
        productNo: null,
        departmentId: null,
        subdepartmentId: null,
        manufacturerId: null,
        cartonSize: 1,
        minOrderQty: 0,
        minStockLevel: 0,
        maxStockLevel: null,
        additionalOrderInformation: null,
        gstCode: "F",
        isPbs: false,
        rrp: null,
        baseSellPrice: 0,
        schedule: null,
        active: false,
        ethical: false,
        locked: false,
        costEx: 0,
        pdes: [],
        apns: [],
        stockGroupIds: [],
        lastModified: null
    },
}

const initialState: StateProps<ManagedStockView> =
{
    ...initialModel,
    gp: 0,
    markup: 0,
    stores: [],
    storeDispensePricing: [],
    showPdePopup: false,
    showApnPopup: false,
    systemStocksPopup: false,
    manualMergePopup: false,
    bulkEditPopup: false,
    bulkEditWizardPopup: false,
    loading: false,
    loadingSync: false,
    saveSuccess: false,
    storeStocks: [],
    stockGroupsDropdown: [],
    keyword: "",
    totalRows: 0,
    totalPages: 0,
    pageIndex: 0,
    pageSize: 100,
    selectedRows: [],
    deselectedRows: [],
    selectedRowsCount: 0,
    error: "",
    selectAllFilter: false,
    table: [],
};

const importWizardState = {
    importBy: "",
    statusPercentage: 10,
    importWizardTableColumns: [],
    importWizardTable: [],
    apnColumn: "",
    pdeColumn: "",
    supplierColumn: "",
    priceColumn: "",
    fileSheets: [],
    columnsList: [],
    currentSheet: "",
    fileWorkBook: [],
    fileLoaded: false,
    readyToProcess: false,
    readyToImport: false,
    isProcessing: false,
    processedStocks: 0,
    totalStocksToImport: 0,
};

interface StockFilter {
    searchPdes: boolean | undefined,
    searchBarcodes: boolean | undefined,
    departmentIdFilter: number | null,
    subdepartmentIdFilter: number | null,
    supplierIdFilter: number | null,
    stockGroupIdFilter: number | null,
    manufacturerIdFilter: number | null,
    activeFilter: boolean | null,
    ethicalFilter: boolean | null,
    scheduleFilter: string | undefined,
    pbsFilter: boolean | null,
}

const filter: StockFilter = {
    searchPdes: undefined,
    searchBarcodes: undefined,
    departmentIdFilter: null,
    subdepartmentIdFilter: null,
    supplierIdFilter: null,
    stockGroupIdFilter: null,
    manufacturerIdFilter: null,
    activeFilter: null,
    ethicalFilter: null,
    scheduleFilter: undefined,
    pbsFilter: null
}

const stocksSlice = createSlice({
    name: "stocksSlice",
    initialState: {
        ...initialState,
        ...importWizardState,
        ...filter,
    },
    reducers: {
        cleanStockDetails: (state) => {
            return {
                ...state,
                ...initialState
            };
        },
        cleanStockModel: (state) => {
            return {
                ...state,
                ...initialModel,
            }
        },
        addStockStore: (state, action) => {
            return {
                ...state,
                stores: state.stores.concat(action.payload),
            };
        },
        addstorestock: (state, action) => {
            return {
                ...state,
                storeStocks: state.storeStocks.concat(action.payload),
            };
        },
        updateStockField: (state, action) => {
            return {
                ...state,
                [action.payload.key]: action.payload.value
            };
        },
        updateModelField: (state, action) => {
            // @ts-ignore
            state.model[action.payload.key] = action.payload.value;
        },
        addSelectedRows: (state, action) => {
            state.selectedRows[action.payload.index] = action.payload.rows;
        },
        addDeselectedRows: (state, action) => {
            state.deselectedRows[action.payload.index] = action.payload.rows;
        },
        addAGGridSelectedRows: (state, action) => {
            return {
                ...state,
                selectedRows: action.payload
            };
        },
        addAGGrideDeselectedRows: (state, action) => {
            return {
                ...state,
                deselectedRows: action.payload
            };
        },
        resetImportWizardState: (state) => {
            return {
                ...state,
                ...importWizardState,
            };
        },
        clearFilters: (state) => {
            return {
                ...state,
                ...filter,
            };
        },
        addImportWizardTable: (state, action) => {
            // @ts-ignore
            state.importWizardTable = state.importWizardTable.concat(action.payload);
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getStocks.fulfilled, (state, action) => {
            return {
                ...state,
                table: action.payload.data,
            };
        });
        builder.addCase(getStock.fulfilled, (state, action) => {
            return {
                ...state,
                model: { ...action.payload.data[0] },
            };
        });
        builder.addCase(getDispensePricing.fulfilled, (state, action) => {
            let data: any[] = [];

            action.payload.stores.forEach((i: any) => {
                if (i.response?.data.length > 0){
                    i.response.data.forEach((r:any) => {
                        data.push({...r, storeName: i.storeName, storeId: i.storeId, errors: i.errors});
                    });
                } else {
                    data.push({storeName: i.storeName, storeId: i.storeId, errors: i.errors});
                }
            });

            return {
                ...state,
                storeDispensePricing: data,
            };
        });
        builder.addCase(updateStock.fulfilled, (state, action) => {
            return {
                ...state,
                ...action.payload,
            };
        });
        builder.addCase(updateMultipleStocks.fulfilled, (state, action) => {
            return {
                ...state,
                ...action.payload,
            };
        });
        builder.addCase(getStockTable.pending, (state, action) => {
            state.loading = true;
            state.error = "";
        });
        builder.addCase(getStockTable.fulfilled, (state, action) => {
            if(state.table.length != 0 && action.payload.data.length != action.payload.totalRows){
                state.table = state.table.concat(action.payload.data);
            } else {
                state.table = action.payload.data ? action.payload.data : [];
            }        
            state.totalRows = action.payload.totalRows;
            state.totalPages = Math.ceil(Math.ceil(action.payload.totalRows) / state.pageSize);
            state.loading = false;
        });
        builder.addCase(getStockTable.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "";
        });
    }
});

export const { addImportWizardTable, clearFilters, resetImportWizardState, updateStockField, cleanStockDetails, cleanStockModel, addStockStore, addSelectedRows, addDeselectedRows, updateModelField, addAGGridSelectedRows, addAGGrideDeselectedRows } = stocksSlice.actions

export default stocksSlice.reducer;